import React, { useEffect, useState } from 'react';
import { InboxIcon, Lightbulb, ClipboardList, Video, AlertTriangle, Clock, CheckCircle2, Send } from 'lucide-react';
import { mccGet } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader, StatCard, EmptyState, StatusPill } from './mccUi';
import { humanDate } from './mccConstants';
import MccContentCard from './MccContentCard';

const BUCKETS = [
  { v: 'overdue', l: 'Overdue', tone: 'rose', icon: AlertTriangle },
  { v: 'due_today', l: 'Due today', tone: 'amber', icon: Clock },
  { v: 'awaiting_approval', l: 'Awaiting approval', tone: 'amber', icon: CheckCircle2 },
  { v: 'changes_required', l: 'Changes required', tone: 'rose', icon: AlertTriangle },
  { v: 'ready_to_publish', l: 'Ready to publish', tone: 'emerald', icon: Send },
  { v: 'in_progress', l: 'In progress', tone: 'sky', icon: null },
  { v: 'recently_completed', l: 'Recently completed', tone: 'emerald', icon: null },
];

export default function MccMyWork({ team, onOpenProject }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async () => {
    setLoading(true);
    try { setData(await mccGet('/marketing/my-work')); }
    catch (e) { toast.error(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return <div className="p-6 text-sm text-slate-500">Loading your work…</div>;

  const buckets = data.buckets || {};
  const hasProjects = Object.values(buckets).some((b) => (b || []).length > 0);

  return (
    <div className="p-6 space-y-6">
      <SectionHeader
        title="My Assigned Work"
        subtitle={`Everything assigned to ${data.me.name} — projects, requests, ideas and filming.`}
        icon={InboxIcon}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Projects" value={data.counts.projects} tone="indigo" icon={ClipboardList} />
        <StatCard label="Requests" value={data.counts.requests} tone="sky" icon={ClipboardList} />
        <StatCard label="Ideas" value={data.counts.ideas} tone="violet" icon={Lightbulb} />
        <StatCard label="Filming" value={data.counts.filming} tone="rose" icon={Video} />
      </div>

      {!hasProjects && (data.ideas.length + data.requests.length + data.filming.length === 0) ? (
        <EmptyState icon={InboxIcon} title="Nothing assigned to you yet"
          body="Once brokers submit requests or ideas get approved and assigned to you, they'll appear here." />
      ) : null}

      {/* Project buckets */}
      {BUCKETS.map(({ v, l, tone, icon: Icon }) => {
        const items = buckets[v] || [];
        if (!items.length) return null;
        return (
          <div key={v}>
            <div className="flex items-center gap-2 mb-2">
              {Icon ? <Icon className="w-4 h-4 text-slate-600" /> : null}
              <p className="text-sm font-bold text-slate-800">{l}</p>
              <span className="text-[10px] font-bold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">{items.length}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
              {items.map((p) => (
                <MccContentCard
                  key={p.id} item={p} nameOf={nameOf}
                  onClick={() => onOpenProject(p.id)}
                  testid={`mcc-mywork-project-${p.id}`}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Requests */}
      {data.requests.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="w-4 h-4 text-slate-600" />
            <p className="text-sm font-bold text-slate-800">Marketing requests</p>
            <span className="text-[10px] font-bold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">{data.requests.length}</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.requests.map((r) => (
              <MccContentCard key={r.id} item={r} variant="request" nameOf={nameOf}
                onClick={() => onOpenProject(r.project_id || null)} />
            ))}
          </div>
        </div>
      )}

      {/* Ideas */}
      {data.ideas.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-slate-600" />
            <p className="text-sm font-bold text-slate-800">Ideas</p>
            <span className="text-[10px] font-bold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">{data.ideas.length}</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.ideas.map((i) => (
              <MccContentCard key={i.id} item={i} variant="idea" nameOf={nameOf}
                onClick={() => toast.info('Ideas open from the Ideas Board — will open there.')} />
            ))}
          </div>
        </div>
      )}

      {/* Filming */}
      {data.filming.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Video className="w-4 h-4 text-slate-600" />
            <p className="text-sm font-bold text-slate-800">Filming</p>
            <span className="text-[10px] font-bold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">{data.filming.length}</span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.filming.map((f) => (
              <div key={f.id} className="border border-slate-200 rounded-xl bg-white p-3 hover:shadow-md transition">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] font-bold text-violet-700 uppercase">{humanDate(f.filming_date)}</span>
                  <StatusPill status={f.status} size="sm" />
                </div>
                <p className="text-sm font-semibold text-slate-900">{f.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{f.person_being_filmed}{f.location ? ` · ${f.location}` : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
